import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableFooter,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { api } from '../api';
import { splitPhones } from '../CounterpartyPanel';
import { PAY_METHODS } from '../PayDialog';
import { usePrintSheet } from '../PrintSheet';
import PrintOutlined from '@mui/icons-material/PrintOutlined';
import { adjustReasons, blendedRate, can, signedPercent } from '@zilian/contracts';
import {
  AdjustRows,
  amount,
  date,
  decimalInput,
  money,
  pickupText,
  Props,
  Tag,
  unitPrice,
  AccessoryPanel,
  accessoriesText,
} from '../ui';

type Form = { kind: 'reconcile' | 'pay' | 'void'; paymentId?: string };

/**
 * A self-recorded order (one this enterprise keeps without the counterparty on the platform),
 * as its own page: summary and steps, the goods with stock in/out progress, then customer
 * sign-off and the money side. Kept apart from the two-party order page at /orders/:id.
 */
export function LocalOrderDetail(p: Props) {
  const { id } = useParams();
  const [order, setOrder] = useState<any>(null);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  useEffect(() => {
    api<any>(`/local-orders/${id}`)
      .then(setOrder)
      .catch((e) => setError(e.message));
  }, [id, p.version, reload]);

  const [form, setForm] = useState<Form | null>(null);
  const printer = usePrintSheet();
  const [printing, setPrinting] = useState(false);
  // Registering who may sign: on its own, or asked before printing a sign-off code.
  const [phones, setPhones] = useState<'edit' | 'print' | null>(null);
  const purchase = order?.kind === 'PURCHASE';
  const verb = purchase ? '入库' : '出库';
  const done = order && Number(order.remaining) <= 0;
  // The server leaves money out for a member who does not see money on this side (e.g. sales
  // staff who also check in purchases): the page then shows goods and their movement only.
  const priced = order?.amount !== undefined;
  // Money side: this enterprise reconciles the amount and records what was received or paid.
  const settlement = order?.settlement;
  const live = (order?.payments ?? []).filter((x: any) => !x.voided_at);
  const paid = live.reduce((sum: number, x: any) => sum + Number(x.amount), 0);
  const left = settlement ? Math.max(0, Number(settlement.amount) - paid) : 0;
  const moved = Number(order?.posted ?? 0) > 0;
  const signed = !!order?.invites?.[0]?.received_at;
  const goods = Number(order?.goods_amount ?? 0);
  const adjustment = Math.round((Number(order?.amount ?? 0) - goods) * 100) / 100;
  const steps: { label: string; done: boolean; optional?: boolean }[] = order
    ? [
        { label: verb, done: done || !!settlement },
        ...(purchase ? [] : [{ label: '客户签收', done: signed, optional: true }]),
        ...(priced
          ? [
              { label: '对账', done: !!settlement },
              { label: purchase ? '付款' : '结清', done: !!settlement && left <= 0 },
            ]
          : []),
      ]
    : [];
  const stage = !priced
    ? done
      ? [`已${verb}`, 'success']
      : [moved ? `部分${verb}` : `待${verb}`, 'warning']
    : !settlement
      ? done
        ? ['待对账', 'warning']
        : [moved ? `部分${verb}` : `待${verb}`, 'warning']
      : left > 0
        ? [purchase ? '待付款' : '待收款', 'warning']
        : ['已完成', 'success'];
  const changed = () => {
    setForm(null);
    setReload((n) => n + 1);
    p.reload();
  };
  // The printed sheet. A sale not yet signed carries the customer sign-off code: printing
  // issues a fresh link (the earlier unsigned one stops working, as with resending).
  const printOrder = async (asked = false) => {
    if (printing) return;
    const sign = !purchase && !signed;
    // Nobody could sign with the code yet: ask for the customer's numbers first, once.
    if (sign && !asked && !order.receiver_phones.length) return setPhones('print');
    setPrinting(true);
    try {
      const code = (path: string) =>
        api<{ image: string }>(`/local-orders/${order.id}/${path}`, {})
          .then((r) => r.image)
          .catch(() => null);
      // A sale also carries the driver's pickup code while goods may still go out.
      const [qr, pickupQr] = await Promise.all([
        sign ? code('sign-code') : null,
        !purchase && !order.settlement ? code('pickup-code') : null,
      ]);
      if (sign) setReload((n) => n + 1);
      printer.print({
        title: purchase ? '采购单' : '送货单',
        issuer: p.current?.name ?? '',
        partyLabel: purchase ? '供货单位' : '收货单位',
        party: order.counterparty,
        number: order.number,
        orderDate: order.created_at,
        deliveryDate: order.delivery_date,
        note: order.note,
        lines: order.lines.map((l: any) => ({
          name: l.sku_snapshot.name,
          spec: l.sku_snapshot.spec,
          unit: l.sku_snapshot.unit,
          quantity: l.quantity,
          unitPrice: l.unit_price,
          amount: l.amount,
          accessories: accessoriesText(l.accessories, l.quantity),
        })),
        adjust: adjustment
          ? {
              amount: adjustment,
              rate: blendedRate(goods, adjustment),
              reasons: adjustReasons(order.lines.map((l: any) => l.adjust)),
            }
          : null,
        qr,
        qrCaption: '收货方扫码签收',
        pickupQr,
        qrNote: !purchase && signed ? '客户已签收' : undefined,
      });
    } finally {
      setPrinting(false);
    }
  };
  // Back to this member's own list: their side's, or 仓库作业 when they only move its goods.
  const roles: string[] = p.current?.roles ?? [];
  const backTo = can(roles, purchase ? 'purchase' : 'sales')
    ? { path: purchase ? '/purchases' : '/sales', label: `${purchase ? '采购' : '销售'}管理` }
    : { path: '/warehouse', label: '仓库作业' };
  const back = (
    <Button
      component={Link}
      to={backTo.path}
      size="small"
      startIcon={<ArrowBack />}
      sx={{ mb: 2 }}
      className="no-print"
    >
      返回{backTo.label}
    </Button>
  );
  if (error)
    return (
      <>
        {back}
        <Alert severity="error">{error}</Alert>
      </>
    );
  if (!order)
    return (
      <Box sx={{ p: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  const due = priced ? left > 0 || !settlement : !done;
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        {back}
        {/* The printed sheet carries prices. */}
        {priced && (
          <Button
            size="small"
            startIcon={<PrintOutlined />}
            disabled={printing}
            onClick={() => void printOrder()}
            sx={{ ml: 'auto' }}
            className="no-print"
          >
            打印
          </Button>
        )}
      </Box>

      {/* Summary, steps and the next step with its action, as on the two-party order page. */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 260 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
              <Tag tone="neutral">{purchase ? '采购单' : '销售单'} · 本企业登记</Tag>
              <Tag tone={stage[1] as any}>{stage[0]}</Tag>
            </Stack>
            <Typography sx={{ fontSize: 24, fontWeight: 700, lineHeight: 1.3 }}>
              {order.counterparty}
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: 13, mt: 0.5 }}>
              登记单号 {order.number}
              {order.note && ` · ${order.note}`}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 5, alignItems: 'flex-end' }}>
            {priced && (
              <Box>
                <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                  单据金额
                </Typography>
                <Typography sx={{ fontSize: 24, fontWeight: 700, lineHeight: 1.3 }}>
                  {money(order.amount)}
                </Typography>
              </Box>
            )}
            <Box>
              <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                交期
              </Typography>
              <Typography sx={{ fontSize: 24, fontWeight: 700, lineHeight: 1.3 }}>
                {date(order.delivery_date)}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ mt: 3, pt: 2.5, borderTop: '1px solid #edf1f2' }}>
          <Steps steps={steps} />
        </Box>
        <Box
          sx={{
            mt: 2.5,
            px: 2,
            py: 1.25,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
            bgcolor: due ? '#fff7ea' : '#eef8f2',
          }}
        >
          <Typography sx={{ fontSize: 14, flex: 1, minWidth: 240 }}>
            {!priced
              ? done
                ? `已全部${verb}。`
                : `按实际${verb}登记，可分批完成。`
              : settlement?.statement_id
                ? `已在对账单 ${order.statement_number} 中${left > 0 ? `，还有 ${money(left)} 未${purchase ? '付' : '收'}` : '，已结清'}。`
                : settlement
                  ? left > 0
                    ? `已对账，还有 ${money(left)} 未${purchase ? '付' : '收'}。`
                    : '已对账并结清。'
                  : done
                    ? `已全部${verb}，可以对账确定金额。`
                    : `按实际${verb}登记，可分批完成；只发一部分时也可以直接对账结束本单。`}
          </Typography>
          {priced && !settlement && !done && moved && (
            <Button color="inherit" onClick={() => setForm({ kind: 'reconcile' })}>
              提前对账
            </Button>
          )}
          {!settlement && !done && (
            <Button variant="contained" onClick={() => p.postLocal(order.id)}>
              登记{verb}
            </Button>
          )}
          {priced && !settlement && done && (
            <Button variant="contained" onClick={() => setForm({ kind: 'reconcile' })}>
              对账
            </Button>
          )}
          {/* On a statement, its amount is the statement's: changed by withdrawing that. */}
          {!priced ? null : settlement?.statement_id ? (
            <Button
              color="inherit"
              component={Link}
              to={`/statements/local/${settlement.statement_id}`}
            >
              查看对账单
            </Button>
          ) : (
            settlement && (
              <Button color="inherit" onClick={() => setForm({ kind: 'reconcile' })}>
                修改金额
              </Button>
            )
          )}
          {settlement && left > 0 && (
            <Button variant="contained" onClick={() => setForm({ kind: 'pay' })}>
              登记{purchase ? '付款' : '收款'}
            </Button>
          )}
        </Box>
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 340px' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        <Stack spacing={3} sx={{ minWidth: 0 }}>
          <Paper sx={{ overflow: 'hidden' }}>
            <div className="panel-title">
              <Typography sx={{ fontSize: 15, fontWeight: 650 }}>
                商品明细 · {order.lines.length} 种
              </Typography>
            </div>
            <Table sx={{ '& th': { whiteSpace: 'nowrap' } }}>
              <TableHead>
                <TableRow>
                  <TableCell>商品</TableCell>
                  <TableCell align="right">数量</TableCell>
                  {priced && <TableCell align="right">单价</TableCell>}
                  {priced && <TableCell align="right">金额</TableCell>}
                  <TableCell sx={{ width: 220 }}>{verb}进度</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {order.lines.map((l: any) => {
                  const pct = Math.round((Number(l.posted) / Number(l.quantity)) * 100);
                  const accessories = l.accessories ?? [];
                  return [
                    <TableRow
                      key={l.id}
                      // A product and its accessories read as one group: no rule between them.
                      sx={accessories.length ? { '& td': { borderBottom: 0 } } : undefined}
                    >
                      <TableCell>
                        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                          {l.sku_snapshot.name}
                        </Typography>
                        <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                          {l.sku_snapshot.spec}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {amount(l.quantity)} {l.sku_snapshot.unit}
                      </TableCell>
                      {priced && (
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          {unitPrice(l.unit_price)}
                          {Number(l.adjust_rate) !== 0 && (
                            <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                              原料 {signedPercent(l.adjust_rate)}
                            </Typography>
                          )}
                        </TableCell>
                      )}
                      {priced && (
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 650 }}>
                          {money(l.amount)}
                        </TableCell>
                      )}
                      <TableCell>
                        <LinearProgress
                          variant="determinate"
                          value={pct}
                          color={pct >= 100 ? 'success' : 'primary'}
                          sx={{ height: 6, borderRadius: 3, bgcolor: '#edf1f2' }}
                        />
                        <Typography color="text.secondary" sx={{ fontSize: 12, mt: 0.75 }}>
                          已{verb} {amount(l.posted)} · 待{verb} {amount(l.remaining)}
                        </Typography>
                      </TableCell>
                    </TableRow>,
                    // Shipped with the product as a whole, so they follow its progress.
                    accessories.length > 0 && (
                      <TableRow key={`${l.id}-accessories`}>
                        <TableCell colSpan={priced ? 5 : 3} sx={{ pt: 0, pb: 1.5 }}>
                          <AccessoryPanel
                            accessories={accessories}
                            productUnit={l.sku_snapshot.unit}
                            pieces={l.quantity}
                            totalLabel="本单共"
                          />
                        </TableCell>
                      </TableRow>
                    ),
                  ];
                })}
              </TableBody>
              {priced && adjustment !== 0 && (
                <TableFooter>
                  <AdjustRows
                    goods={goods}
                    adjustment={adjustment}
                    reasons={adjustReasons(order.lines.map((l: any) => l.adjust))}
                    span={3}
                  />
                  <TableRow>
                    <TableCell colSpan={3} sx={{ fontSize: 13, color: 'text.secondary' }}>
                      合计
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: 'text.primary',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {money(order.amount)}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </Paper>
          <Batches order={order} purchase={purchase} verb={verb} />
        </Stack>
        <Stack spacing={3}>
          {!purchase && (
            <CustomerSign
              order={order}
              onPhones={() => setPhones('edit')}
              onRevoke={(inviteId) =>
                api(`/local-orders/${order.id}/invites/${inviteId}/revoke`, {})
                  .then(() => setReload((n) => n + 1))
                  .catch((e) => setError(e.message))
              }
            />
          )}
          {priced && (
            <Money
              order={order}
              purchase={purchase}
              paid={paid}
              left={left}
              onVoid={(paymentId) => setForm({ kind: 'void', paymentId })}
            />
          )}
        </Stack>
      </Box>
      {phones && (
        <PhonesDialog
          order={order}
          printing={phones === 'print'}
          onClose={() => setPhones(null)}
          onDone={(saved) => {
            const print = phones === 'print';
            setPhones(null);
            if (saved) setReload((n) => n + 1);
            if (print) void printOrder(true);
          }}
        />
      )}
      {printer.sheet}
      {form && (
        <MoneyDialog
          form={form}
          order={order}
          purchase={purchase}
          left={left}
          paid={paid}
          onClose={() => setForm(null)}
          onDone={changed}
        />
      )}
    </>
  );
}

function Steps({ steps }: { steps: { label: string; done: boolean; optional?: boolean }[] }) {
  const current = steps.findIndex((s) => !s.done && !s.optional);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {steps.map((step, i) => {
        const now = i === current;
        const skipped = step.optional && !step.done && (current === -1 || current > i);
        return (
          <Box
            key={step.label}
            sx={{ display: 'flex', alignItems: 'center', flex: i ? 1 : 'none', minWidth: 0 }}
          >
            {i > 0 && (
              <Box
                sx={{
                  flex: 1,
                  height: 2,
                  mx: 1,
                  minWidth: 8,
                  bgcolor: step.done || now ? 'primary.main' : '#e3e9eb',
                }}
              />
            )}
            <Box
              sx={{
                width: 20,
                height: 20,
                flex: 'none',
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                fontSize: 11,
                fontWeight: 700,
                bgcolor: step.done ? 'primary.main' : now ? '#e8f3f0' : '#f1f4f5',
                color: step.done ? '#fff' : now ? 'primary.main' : '#9aa8ad',
                boxShadow: now ? '0 0 0 2px #17665f' : 'none',
              }}
            >
              {step.done ? '✓' : i + 1}
            </Box>
            <Typography
              noWrap
              sx={{
                ml: 0.75,
                fontSize: 12.5,
                flex: 'none',
                fontWeight: now ? 700 : 500,
                color: step.done ? 'primary.main' : now ? 'text.primary' : 'text.secondary',
              }}
            >
              {step.label}
              {step.optional && !step.done && (
                <Box component="span" sx={{ color: 'text.disabled', ml: 0.5 }}>
                  {skipped ? '未签' : '可选'}
                </Box>
              )}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

/** Reconciled amount, what has been received or paid against it, and each entry. */
/** Each registration of stock in or out, newest first; for a sale, who took the goods. */
function Batches({ order, purchase, verb }: { order: any; purchase: boolean; verb: string }) {
  const lineOf = (lineId: string) => order.lines.find((l: any) => l.id === lineId);
  return (
    <Paper sx={{ overflow: 'hidden' }}>
      <div className="panel-title">
        <Typography sx={{ fontSize: 15, fontWeight: 650 }}>{verb}记录</Typography>
      </div>
      {order.batches.length ? (
        order.batches.map((b: any) => (
          <Box key={b.created_at} sx={{ px: 3, py: 2, borderBottom: '1px solid #edf1f2' }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
              {new Date(b.created_at).toLocaleString('zh-CN', { hour12: false })} {verb}
              <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                {b.actor_name ? ` · ${b.actor_name}登记` : ''}
              </Box>
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: 12.5, mt: 0.75 }}>
              {b.lines
                .map((l: any) => {
                  const line = lineOf(l.lineId);
                  return `${line?.sku_snapshot.name ?? '商品'} ${amount(l.quantity)} ${line?.sku_snapshot.unit ?? ''}`;
                })
                .join('、')}
            </Typography>
            {/* Once the customer signed or the order is reconciled, the goods have arrived. */}
            {!purchase && (b.pickup || !(order.settlement || order.invites?.[0]?.received_at)) && (
              <Typography
                sx={{
                  fontSize: 12,
                  mt: 0.75,
                  color: b.pickup ? 'text.secondary' : 'text.disabled',
                }}
              >
                {b.pickup ? `提货：${pickupText(b.pickup)}` : '待司机扫码确认提货'}
              </Typography>
            )}
          </Box>
        ))
      ) : (
        <Typography color="text.secondary" sx={{ fontSize: 12.5, px: 3, py: 2.5 }}>
          还没有{verb}。{purchase ? '' : '打印的送货单上有司机提货码，装车后由司机扫码确认。'}
        </Typography>
      )}
    </Paper>
  );
}

function Money(p: {
  order: any;
  purchase: boolean;
  paid: number;
  left: number;
  onVoid: (paymentId: string) => void;
}) {
  const { order, purchase } = p;
  const settlement = order.settlement;
  const verb = purchase ? '付' : '收';
  if (!settlement && Number(order.posted) <= 0) return null;
  return (
    <Paper sx={{ p: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 650, flex: 1 }}>
          对账与{purchase ? '付款' : '收款'}
        </Typography>
        {settlement && (
          <Tag tone={p.left > 0 ? 'warning' : 'success'}>
            {p.left > 0 ? `待${verb} ${money(p.left)}` : '已结清'}
          </Tag>
        )}
      </Box>
      {!settlement ? (
        <Typography color="text.secondary" sx={{ fontSize: 12.5, mt: 1, lineHeight: 1.7 }}>
          尚未对账。按已{purchase ? '入库' : '出库'}数量和单价
          {!purchase && order.invites?.[0]?.received_at ? '、扣除客户签收短少' : ''}，应为{' '}
          <b>{money(order.suggested_amount)}</b>。
        </Typography>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mt: 1 }}>
            <Typography sx={{ fontSize: 22, fontWeight: 700 }}>
              {money(settlement.amount)}
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: 12 }}>
              已{verb} {money(p.paid)}
              {settlement.note && ` · ${settlement.note}`}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, (p.paid / Math.max(Number(settlement.amount), 0.01)) * 100)}
            color="success"
            sx={{ height: 6, borderRadius: 3, bgcolor: '#edf1f2', mt: 1 }}
          />
          {order.payments.length > 0 && (
            <Stack spacing={0.75} sx={{ mt: 1.5 }}>
              {order.payments.map((x: any) => (
                <Box
                  key={x.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: 13,
                    color: x.voided_at ? 'text.disabled' : 'text.primary',
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      component="span"
                      sx={{
                        fontWeight: 650,
                        textDecoration: x.voided_at ? 'line-through' : 'none',
                      }}
                    >
                      {money(x.amount)}
                    </Box>{' '}
                    · {PAY_METHODS[x.method]}
                    {x.reference && ` · ${x.reference}`}
                    <Box component="span" sx={{ color: 'text.secondary', ml: 1, fontSize: 12 }}>
                      {new Date(x.created_at).toLocaleDateString('zh-CN')}
                      {x.voided_at && ` · 已作废：${x.void_reason}`}
                    </Box>
                  </Box>
                  {/* A share of a statement payment is voided with it, on the statement. */}
                  {!x.voided_at && !x.statement_payment_id && (
                    <Button size="small" color="inherit" onClick={() => p.onVoid(x.id)}>
                      作废
                    </Button>
                  )}
                </Box>
              ))}
            </Stack>
          )}
        </>
      )}
    </Paper>
  );
}

/** Reconcile, record a payment, or void one; each a small form against this order. */
function MoneyDialog(p: {
  form: Form;
  order: any;
  purchase: boolean;
  left: number;
  paid: number;
  onClose: () => void;
  onDone: () => void;
}) {
  const { form, order, purchase } = p;
  const [amountText, setAmountText] = useState(
    form.kind === 'reconcile'
      ? (order.settlement?.amount ?? Number(order.suggested_amount).toFixed(2))
      : p.left.toFixed(2),
  );
  const [method, setMethod] = useState('BANK');
  const [text, setText] = useState(form.kind === 'reconcile' ? (order.settlement?.note ?? '') : '');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [key] = useState(() => crypto.randomUUID());
  const verb = purchase ? '付' : '收';
  const partial = form.kind === 'reconcile' && Number(order.remaining) > 0;
  const title =
    form.kind === 'reconcile'
      ? order.settlement
        ? '修改对账金额'
        : '对账'
      : form.kind === 'pay'
        ? `登记${purchase ? '付款' : '收款'}`
        : '作废这笔记录';
  async function submit() {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const base = `/local-orders/${order.id}`;
      if (form.kind === 'reconcile')
        await api(`${base}/reconcile`, { amount: amountText, note: text });
      else if (form.kind === 'pay')
        await api(`${base}/payments`, { amount: amountText, method, reference: text, key });
      else await api(`${base}/payments/${form.paymentId}/void`, { reason: text });
      p.onDone();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open fullWidth maxWidth="xs" onClose={() => !busy && p.onClose()}>
      <DialogTitle sx={{ pb: 1 }}>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Typography color="text.secondary" sx={{ fontSize: 13, lineHeight: 1.7 }}>
            {form.kind === 'reconcile'
              ? `按实际${purchase ? '入库' : '出库'}应为 ${money(order.suggested_amount)}，可按双方约定调整。${
                  partial
                    ? `对账后本单结束，剩余 ${amount(order.remaining)} 不再${purchase ? '入库' : '出库'}。`
                    : ''
                }${p.paid > 0 ? `已${verb} ${money(p.paid)}，金额不能低于此数。` : ''}`
              : form.kind === 'pay'
                ? `未${verb} ${money(p.left)}。款项在双方之间线下${purchase ? '支付' : '收取'}，这里只做记录。`
                : '作废后不计入已收付金额，记录和原因会保留。'}
          </Typography>
          {form.kind !== 'void' && (
            <TextField
              label={form.kind === 'reconcile' ? '对账金额' : `${verb}款金额`}
              value={amountText}
              disabled={busy}
              onChange={(e) => setAmountText(decimalInput(e.target.value, 2))}
              slotProps={{
                input: { startAdornment: <InputAdornment position="start">¥</InputAdornment> },
              }}
            />
          )}
          {form.kind === 'pay' && (
            <TextField
              select
              label={`${verb}款方式`}
              value={method}
              disabled={busy}
              onChange={(e) => setMethod(e.target.value)}
            >
              {Object.entries(PAY_METHODS).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            label={
              form.kind === 'reconcile'
                ? '说明（选填）'
                : form.kind === 'pay'
                  ? '流水号 / 备注（选填）'
                  : '作废原因'
            }
            value={text}
            disabled={busy}
            onChange={(e) => setText(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button disabled={busy} onClick={p.onClose}>
          取消
        </Button>
        <Button
          variant="contained"
          color={form.kind === 'void' ? 'error' : 'primary'}
          disabled={busy}
          onClick={submit}
        >
          {busy ? '正在提交…' : '确认'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Where the customer stands on the latest WeChat link: sent, opened, confirmed, signed (with
 * what actually arrived) and whether it has joined as a partner enterprise.
 */
/**
 * The customer's mobile numbers: signing takes a WeChat that verifies one of them. Asked before
 * printing a sign-off code when none is registered; printing without them stays possible.
 */
function PhonesDialog(p: {
  order: any;
  printing: boolean;
  onClose: () => void;
  onDone: (saved: boolean) => void;
}) {
  const [text, setText] = useState(p.order.receiver_phones.join(' '));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  async function save() {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await api(`/counterparties/${p.order.counterparty_id}/receiver-phones`, {
        phones: splitPhones(text),
      });
      p.onDone(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open fullWidth maxWidth="xs" onClose={() => !busy && p.onClose()}>
      <DialogTitle sx={{ pb: 1 }}>{p.printing ? '登记对接手机号' : '对接手机号'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Typography color="text.secondary" sx={{ fontSize: 13, lineHeight: 1.7 }}>
            {p.order.counterparty} 扫码签收时，需用以下号码的微信验证。
            {p.printing ? '也可以先打印，签收前补上。' : ''}
          </Typography>
          <TextField
            label="对接手机号"
            helperText="最多 3 个，空格分隔"
            autoFocus
            value={text}
            disabled={busy}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void save()}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button disabled={busy} onClick={() => (p.printing ? p.onDone(false) : p.onClose())}>
          {p.printing ? '直接打印' : '取消'}
        </Button>
        <Button variant="contained" disabled={busy} onClick={save}>
          {busy ? '正在保存…' : p.printing ? '保存并打印' : '保存'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CustomerSign({
  order,
  onPhones,
  onRevoke,
}: {
  order: any;
  onPhones: () => void;
  onRevoke: (inviteId: string) => void;
}) {
  const invite = order.invites?.[0];
  const when = (v: string) => new Date(v).toLocaleString('zh-CN', { hour12: false }).slice(0, -3);
  const live = invite && !invite.revoked_at && new Date(invite.expires_at) > new Date();
  const name = (lineId: string) => order.lines.find((l: any) => l.id === lineId)?.sku_snapshot;
  const status: [string, 'success' | 'info' | 'warning' | 'neutral'] = !invite
    ? ['未发送', 'neutral']
    : invite.received_at
      ? ['客户已签收', 'success']
      : !live
        ? [invite.revoked_at ? '链接已撤回' : '链接已过期', 'neutral']
        : invite.confirmed_at
          ? ['客户已确认订单', 'info']
          : invite.opened_at
            ? ['客户已打开', 'info']
            : ['等待客户打开', 'warning'];
  return (
    <Paper sx={{ p: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 650, flex: 1 }}>客户确认与签收</Typography>
        {invite?.joined_name && <Tag tone="success">已开通联仓通</Tag>}
        <Tag tone={status[1]}>{status[0]}</Tag>
      </Box>
      {!invite?.received_at && (
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            sx={{
              fontSize: 12.5,
              flex: 1,
              color: order.receiver_phones.length ? 'text.primary' : 'warning.dark',
            }}
          >
            {order.receiver_phones.length
              ? `对接手机号 ${order.receiver_phones.join('、')}`
              : '未登记对接手机号，客户扫码后无法签收'}
          </Typography>
          <Button size="small" onClick={onPhones}>
            {order.receiver_phones.length ? '修改' : '登记'}
          </Button>
        </Box>
      )}
      {!invite ? (
        <Typography color="text.secondary" sx={{ fontSize: 12.5, mt: 1, lineHeight: 1.7 }}>
          在小程序中打开这张单据，点“发给客户确认”，把卡片发到客户微信。客户无需注册即可确认订单、登记实收，签收结果会回到这里。
        </Typography>
      ) : (
        <>
          <Typography color="text.secondary" sx={{ fontSize: 12, mt: 1 }}>
            {[
              `${when(invite.created_at)} 发出`,
              invite.opened_at && `${when(invite.opened_at)} 打开`,
              invite.confirmed_at && !invite.received_at && `${when(invite.confirmed_at)} 确认`,
              invite.received_at &&
                `${when(invite.received_at)} 由 ${invite.signer || '客户'}${invite.verified_phone ? `（${invite.verified_phone}）` : ''} 签收`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
          {invite.lines.length > 0 && (
            <Stack spacing={0.5} sx={{ mt: 1.25 }}>
              {invite.lines.map((r: any) => {
                const sku = name(r.lineId);
                const short = Number(r.shipped) - Number(r.received);
                return (
                  <Box key={r.lineId} sx={{ display: 'flex', fontSize: 13, gap: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>{sku?.name}</Box>
                    <Box sx={{ color: 'text.secondary' }}>发 {amount(r.shipped)}</Box>
                    <Box
                      sx={{ fontWeight: 650, color: short > 0 ? 'warning.dark' : 'text.primary' }}
                    >
                      收 {amount(r.received)}
                      {short > 0 && ` · 少 ${amount(short)}`} {sku?.unit}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}
          {invite.receipt_note && (
            <Typography sx={{ fontSize: 12.5, mt: 1 }}>备注：{invite.receipt_note}</Typography>
          )}
          {invite.joined_name && (
            <Typography color="text.secondary" sx={{ fontSize: 12.5, mt: 1 }}>
              客户已开通“{invite.joined_name}”并成为合作企业，之后可直接给它下协同订单。
            </Typography>
          )}
          {!live && !invite.received_at && (
            <Typography color="text.secondary" sx={{ fontSize: 12, mt: 1 }}>
              需要客户确认时，在小程序中打开这张单据重新发送。
            </Typography>
          )}
          {live && !invite.received_at && (
            <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography color="text.secondary" sx={{ fontSize: 12, flex: 1 }}>
                链接 {new Date(invite.expires_at).toLocaleDateString('zh-CN')} 前有效
              </Typography>
              <Button size="small" color="error" onClick={() => onRevoke(invite.id)}>
                撤回链接
              </Button>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
}
